import React from 'react';
import { Box, PendingIcon, Cluster, ButtonWithAction } from '@thecb/components';
import {
  CancelledIcon,
  SuccessfulIcon,
  VoidedIcon,
  OnHoldIcon,
  IncompleteIcon,
  OpenedIcon,
} from '../status-icons';
import { RefundedIcon } from '../status-icons/RefundedIcon';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getOrderDetailsRequest } from '../../pages/order-details/OrderDetails.state';
import { v4 as uuidv4 } from 'uuid';
import { ORDER_STATUS } from '../../utils/constants';
import { constants } from '@thecb/components';

const INCOMPLETE_STATUS_TEXT = 'Order Incomplete';
const VOIDED_STATUS_TEXT = 'Order Voided';
const VOID_PENDING_STATUS_TEXT = 'Void in Progress';
const CANCELED_STATUS_TEXT = 'Order Cancelled';
const COMPLETED_STATUS_TEXT = 'Successful';
const REFUNDED_ORDER_TEXT = 'Refunded order';
const ORIGINAL_ORDER_TEXT = 'Original order';
const OPENED_STATUS_TEXT = 'Order Opened';
const HOLD_STATUS_TEXT = 'Order on Hold';
const PAYLINK_SENT_STATUS_TEXT = 'PayLink Sent';
const PAYLINK_COMPLETED_STATUS_TEXT = 'PayLink Completed';
const DEFAULT_STATUS_TEXT = 'N/A';

export const StatusModule = ({ status = '', label }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { parent_id = null, child_ids = [] } = {
    ...useSelector((state) => state.orderDetails.data.order), // allows us to ignore destructuring of undefined while redux store populates
  };
  const relatedOrderIdsArr = parent_id ? [parent_id] : child_ids;
  const getOrderAction =
    (id, getOrderDetails = false) =>
    () => {
      if (getOrderDetails) {
        dispatch(getOrderDetailsRequest({ id }));
      }
      navigate(`/pos/order/${id}`);
    };
  const getCompletedLabelText = () => {
    if (parent_id) {
      return `${REFUNDED_ORDER_TEXT} | ${ORIGINAL_ORDER_TEXT}:`;
    } else {
      return child_ids?.length
        ? `${label} ${COMPLETED_STATUS_TEXT} | ${REFUNDED_ORDER_TEXT}${
            child_ids.length > 1 ? 's' : ''
          }:`
        : `${label} ${COMPLETED_STATUS_TEXT}`;
    }
  };

  let icon = '';
  let orderInfo = '';

  switch (status) {
    case ORDER_STATUS.INCOMPLETE:
      orderInfo = INCOMPLETE_STATUS_TEXT;
      icon = <IncompleteIcon />;
      break;
    case ORDER_STATUS.VOIDED:
      orderInfo = VOIDED_STATUS_TEXT;
      icon = <VoidedIcon />;
      break;
    case ORDER_STATUS.VOID_PENDING:
      orderInfo = VOID_PENDING_STATUS_TEXT;
      icon = <PendingIcon fill={constants.colors.LUCKY_POINT} />;
      break;
    case ORDER_STATUS.CANCELLED:
      orderInfo = CANCELED_STATUS_TEXT;
      icon = <CancelledIcon />;
      break;
    case ORDER_STATUS.COMPLETED:
      icon = parent_id ? <RefundedIcon /> : <SuccessfulIcon />;
      orderInfo = (
        <Cluster align="center">
          <Box padding="0px">{getCompletedLabelText()}</Box>
          {relatedOrderIdsArr &&
            relatedOrderIdsArr.map((id, index) => (
              <Box padding="0" key={`${id}-${uuidv4()}`}>
                <ButtonWithAction
                  text={id}
                  variant="smallGhost"
                  action={getOrderAction(id, !!parent_id)}
                  extraStyles={`min-width: 0px; margin: 0rem 0.25rem;`}
                  textExtraStyles="font-size: 1rem;"
                />
                <>{index < relatedOrderIdsArr.length - 1 && ', '}</>
              </Box>
            ))}
        </Cluster>
      );
      break;
    case ORDER_STATUS.OPENED:
      orderInfo = OPENED_STATUS_TEXT;
      icon = <OpenedIcon />;
      break;
    case ORDER_STATUS.HOLD:
      orderInfo = HOLD_STATUS_TEXT;
      icon = <OnHoldIcon />;
      break;
    case ORDER_STATUS.PAYLINK_SENT:
      orderInfo = PAYLINK_SENT_STATUS_TEXT;
      icon = <PendingIcon fill={constants.colors.LUCKY_POINT} />;
      break;
    case ORDER_STATUS.PAYLINK_COMPLETED:
      orderInfo = PAYLINK_COMPLETED_STATUS_TEXT;
      icon = <SuccessfulIcon />;
      break;
    default:
      orderInfo = DEFAULT_STATUS_TEXT;
      icon = <PendingIcon />;
  }

  return (
    <Box padding="0" id="order-status-module">
      <Cluster align="center">
        {icon}
        <Box padding="0px 0px 0px .75rem;">{orderInfo}</Box>
      </Cluster>
    </Box>
  );
};
